import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useProspects, type Prospect } from '@/hooks/useProspects';

interface DeleteConfirmDialogProps {
  prospect: Prospect;
  trigger?: React.ReactNode;
}

export function DeleteConfirmDialog({ prospect, trigger }: DeleteConfirmDialogProps) {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { removeProspect } = useProspects();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await removeProspect(prospect._id);
      toast.success(`${prospect.company} has been deleted`);
      setOpen(false);
    } catch (error) {
      // Log error details in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to delete prospect:', error);
      }
      toast.error('Failed to delete prospect. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Prospect</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{prospect.company}</strong>?
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="space-y-2 text-sm">
            <p><strong>Company:</strong> {prospect.company}</p>
            <p><strong>Contact:</strong> {prospect.contactName}</p>
            <p><strong>Email:</strong> {prospect.email}</p>
            <p><strong>Stage:</strong> {prospect.stage}</p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}